import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------*/
const dimensionColor = (dimension) => {
  switch (dimension.toLowerCase()) {
    case 'access':
      return 'primary';
    case 'inclusion':
      return 'success';
    case 'representation':
      return 'warning';
    case 'fairness':
      return 'info';
    case 'trust':
      return 'secondary';
    default:
      return 'default';
  }
};

/* -------------------------------------------------------------
 * Robust JSON parser – returns an object or an empty one.
 * -----------------------------------------------------------*/
const safeParse = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data; // Already parsed
  try {
    return JSON.parse(data);
  } catch (err) {
    console.error('Equity‑Audit results JSON malformed:', err);
    return {};
  }
};

/** -------------------------------------------------------------
 * EquityAuditAnalysis – rich visualisation for equity audit output
 * ------------------------------------------------------------*/
export default function EquityAuditAnalysis({ item }) {
  if (!item) return null;

  // Parse results safely
  const results = safeParse(item.results);

  /* ──────────────────────── Score distribution ─────────────────────── */
  const ScoreDistribution = () => {
    const dist = results.score_distribution;
    if (!dist) return null;
    const bands = [
      { label: 'High (≥80)', value: dist.high, color: 'success' },
      { label: 'Moderate (60–79)', value: dist.moderate, color: 'warning' },
      { label: 'Low (<60)', value: dist.low, color: 'error' },
    ];
    return (
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Score Distribution
          </Typography>
          <Grid container spacing={2}>
            {bands.map((b) => (
              <Grid item xs={4} key={b.label}>
                <Chip
                  label={`${b.label}: ${b.value}`}
                  size="small"
                  color={b.color}
                  sx={{ width: '100%', borderRadius: '4px', py: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  /* ───────────────────── Priority actions list ────────────────────── */
  const PriorityActions = () => (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Priority Actions
        </Typography>
        <List dense>
          {results.priority_actions?.map((a, i) => (
            <ListItem key={i} disableGutters>
              <ListItemText primary={a} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  /* ────────────────────── Dimension accordion ─────────────────────── */
  const DimensionItem = ({ dim }) => {
    const color = dimensionColor(dim.dimension);
    return (
      <Accordion variant="outlined" disableGutters sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: 'background.default', borderRadius: 1 }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              {dim.dimension}
            </Typography>
            <Chip label={`Mean: ${dim.mean_equity_score}`} size="small" color={color} />
            <Chip
              label={`Range: ${dim.min_equity_score}–${dim.max_equity_score}`}
              size="small"
              color={color}
              variant="outlined"
            />
            <Chip
              label={`Severity: ${dim.mean_severity}`}
              size="small"
              color={color}
              variant="outlined"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {dim.predominant_themes?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Predominant Themes</Typography>
              <List dense>
                {dim.predominant_themes.map((t, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={t} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {dim.common_gaps?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Common Gaps</Typography>
              <List dense>
                {dim.common_gaps.map((g, i) => (
                  <ListItem key={i} disableGutters>
                    <WarningAmberIcon fontSize="inherit" sx={{ mr: 1 }} />
                    <ListItemText primary={g} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {dim.recommended_actions?.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Recommended Actions</Typography>
              <List dense sx={{ pl: 2 }}>
                {dim.recommended_actions.map((a, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={a} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };
  DimensionItem.propTypes = {
    dim: PropTypes.object.isRequired,
  };

  /* ─────────────────────────── Render ─────────────────────────── */
  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Equity Audit Analysis' }}>
      {/* --- Main Title --- */}
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <GavelIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Equity Audit Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Analysis performed on{' '}
            {new Date(item.created_at?.$date || item.created_at).toLocaleString()}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        <ScoreDistribution />
        {results.priority_actions?.length > 0 && <PriorityActions />}

        {/* Dimensions */}
        {results.dimensions_summary?.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Dimensions Analysis
            </Typography>
            {results.dimensions_summary.map((d) => (
              <DimensionItem key={d.dimension} dim={d} />
            ))}
          </Box>
        )}
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
}

EquityAuditAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
